import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { UnitTree } from '../oss/unit-tree.jsx';
import { OssDetail } from '../oss/unit-detail.jsx';
import { UnitFormModal } from '../oss/unit-form-modal.jsx';
import { PersonFormModal } from '../oss/person-form-modal.jsx';
import { MoveModal } from '../oss/move-modal.jsx';
import { api, ApiError } from '../../api/index.js';

/**
 * Flattens the tree into [{ id, name, depth, disabled }] for select pickers.
 * `excludeSubtreeOf` keeps the calling unit's subtree out (used in unit move).
 */
function flattenForSelect(tree, excludeSubtreeOf = null) {
  const out = [];
  const excluded = new Set();
  function walk(unit, depth, withinExcluded) {
    const isExcl = withinExcluded || (excludeSubtreeOf != null && unit.id === excludeSubtreeOf);
    if (isExcl) excluded.add(unit.id);
    out.push({ id: unit.id, name: unit.name, depth, disabled: isExcl });
    for (const c of unit.children) walk(c, depth + 1, isExcl);
  }
  for (const r of tree) walk(r, 0, false);
  return out;
}

function buildLookups(tree) {
  const units = {};
  const persons = {};
  function walk(u) {
    units[u.id] = u;
    for (const p of u.persons) persons[p.id] = p;
    for (const c of u.children) walk(c);
  }
  for (const r of tree) walk(r);
  return { units, persons };
}

function findAncestry(tree, unitId) {
  const path = [];
  function walk(u, trail) {
    const next = [...trail, u];
    if (u.id === unitId) {
      path.push(...next);
      return true;
    }
    for (const c of u.children) {
      if (walk(c, next)) return true;
    }
    return false;
  }
  for (const r of tree) {
    if (walk(r, [])) break;
  }
  return path;
}

function inheritedHomes(tree, nodes) {
  // For each unit, find the nearest ancestor with home_node_id and use it.
  const map = {};
  function walk(u, inherited) {
    const own = u.homeNodeId ? { id: u.homeNodeId, name: nodes[u.homeNodeId]?.name || `#${u.homeNodeId}` } : null;
    const effective = own || inherited;
    if (!u.homeNodeId && inherited) map[u.id] = inherited;
    for (const c of u.children) walk(c, effective);
  }
  for (const r of tree) walk(r, null);
  return map;
}

export function OssPage({ currentUser }) {
  const toast = useToast();
  const isAdmin = currentUser?.role === 'admin';
  const fileInputRef = useRef(null);

  const [tree, setTree] = useState([]);
  const [stats, setStats] = useState(null);
  const [nodesById, setNodesById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState(null);

  const [unitFormState, setUnitFormState] = useState({ open: false, mode: 'create', parent: null, initial: null });
  const [personFormState, setPersonFormState] = useState({ open: false, mode: 'create', unit: null, initial: null });
  const [moveState, setMoveState] = useState({ open: false, mode: 'unit', item: null });
  const [deleteState, setDeleteState] = useState({ open: false, kind: null, item: null });
  const [importing, setImporting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [unitsRes, nodesRes] = await Promise.all([
        api.units.tree(),
        api.nodes.list(),
      ]);
      setTree(unitsRes.tree || []);
      setStats(unitsRes.stats || null);
      const nMap = {};
      for (const n of nodesRes.nodes || []) nMap[n.id] = n;
      setNodesById(nMap);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const lookups = useMemo(() => buildLookups(tree), [tree]);
  const ancestry = useMemo(
    () => (selection?.kind === 'unit' ? findAncestry(tree, selection.id) : []),
    [tree, selection],
  );
  const inheritedMap = useMemo(() => inheritedHomes(tree, nodesById), [tree, nodesById]);

  // ---------------- Unit handlers ----------------
  function openCreateRoot() {
    setUnitFormState({ open: true, mode: 'create', parent: null, initial: null });
  }
  function openCreateChild(parent) {
    setUnitFormState({ open: true, mode: 'create', parent, initial: null });
  }
  function openEditUnit(unit) {
    setUnitFormState({ open: true, mode: 'edit', parent: null, initial: unit });
  }
  function openMoveUnit(unit) {
    setMoveState({ open: true, mode: 'unit', item: unit });
  }
  function openDeleteUnit(unit) {
    setDeleteState({ open: true, kind: 'unit', item: unit });
  }

  async function handleUnitSave(payload) {
    if (unitFormState.mode === 'create') {
      await api.units.create({ ...payload, parentId: unitFormState.parent?.id || null });
      toast.success(`Unit "${payload.name}" created.`);
    } else {
      await api.units.update(unitFormState.initial.id, payload);
      toast.success('Unit updated.');
    }
    setUnitFormState({ open: false, mode: 'create', parent: null, initial: null });
    await refresh();
  }

  // ---------------- Person handlers ----------------
  function openAddPerson(unit) {
    setPersonFormState({ open: true, mode: 'create', unit, initial: null });
  }
  function openEditPerson(person, unit) {
    setPersonFormState({ open: true, mode: 'edit', unit, initial: person });
  }
  function openMovePerson(person) {
    setMoveState({ open: true, mode: 'person', item: person });
  }
  function openDeletePerson(person) {
    setDeleteState({ open: true, kind: 'person', item: person });
  }

  async function handlePersonSave(payload) {
    if (personFormState.mode === 'create') {
      await api.persons.create({ ...payload, unitId: personFormState.unit.id });
      toast.success(`Person "${payload.displayName}" added.`);
    } else {
      await api.persons.update(personFormState.initial.id, payload);
      toast.success('Person updated.');
    }
    setPersonFormState({ open: false, mode: 'create', unit: null, initial: null });
    await refresh();
  }

  // ---------------- Move ----------------
  async function handleMoveConfirm(targetId) {
    const { mode, item } = moveState;
    if (mode === 'unit') {
      await api.units.move(item.id, targetId);
      toast.success(`Unit "${item.name}" moved.`);
    } else {
      await api.persons.move(item.id, targetId);
      toast.success(`Person "${item.displayName}" moved.`);
    }
    setMoveState({ open: false, mode: 'unit', item: null });
    await refresh();
  }

  // ---------------- Delete ----------------
  async function handleDelete() {
    const { kind, item } = deleteState;
    try {
      if (kind === 'unit') {
        await api.units.remove(item.id);
        toast.success(`Unit "${item.name}" deleted.`);
        if (selection?.kind === 'unit' && selection.id === item.id) setSelection(null);
      } else {
        await api.persons.remove(item.id);
        toast.success(`Person "${item.displayName}" deleted.`);
        if (selection?.kind === 'person' && selection.id === item.id) setSelection(null);
      }
      setDeleteState({ open: false, kind: null, item: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    }
  }

  // ---------------- Import / Export ----------------
  function handleImportClick() {
    fileInputRef.current?.click();
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 18 * 1024 * 1024) {
      toast.error('File too large (max 18 MB).');
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      const doc = JSON.parse(text);
      const { summary } = await api.oss.importDoc(doc);
      const parts = [];
      if (summary.unitsCreated) parts.push(`${summary.unitsCreated} unit(s) created`);
      if (summary.unitsUpdated) parts.push(`${summary.unitsUpdated} updated`);
      if (summary.personsCreated) parts.push(`${summary.personsCreated} person(s) created`);
      if (summary.personsUpdated) parts.push(`${summary.personsUpdated} updated`);
      toast.success(parts.length ? `OSS imported: ${parts.join(', ')}.` : 'OSS imported (nothing changed).');
      if (summary.warnings?.length) {
        toast.warning(`${summary.warnings.length} warning(s) — check audit log.`);
      }
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Import failed.';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  }

  // ---------------- Render ----------------
  const moveFlatUnits = useMemo(() => {
    if (!moveState.open) return [];
    if (moveState.mode === 'unit') {
      return flattenForSelect(tree, moveState.item?.id);
    }
    return flattenForSelect(tree); // person move: any unit
  }, [tree, moveState]);

  return (
    <PageShell
      icon="fa-sitemap"
      title="OSS"
      subtitle="Organizational and Staffing Structure — units and personnel"
      actions={
        <div className="flex items-center gap-2">
          <a
            href={api.oss.exportUrl}
            download="tms-oss.json"
            className="btn-secondary"
            title="Download the entire OSS tree as JSON"
          >
            <i className="fa-solid fa-file-arrow-down" aria-hidden="true" /> Export
          </a>
          {isAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={handleImportFile}
              />
              <button
                type="button"
                onClick={handleImportClick}
                disabled={importing}
                className="btn-secondary"
                title="Import a previously exported OSS JSON"
              >
                <i className={`fa-solid ${importing ? 'fa-spinner fa-spin' : 'fa-file-arrow-up'}`} aria-hidden="true" />
                {importing ? 'Importing…' : 'Import'}
              </button>
              <button type="button" onClick={openCreateRoot} className="btn-primary">
                <i className="fa-solid fa-folder-plus" aria-hidden="true" /> New root unit
              </button>
            </>
          )}
        </div>
      }
    >
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard icon="fa-sitemap"          label="Units"             value={stats.units}             tone="accent" />
          <StatCard icon="fa-folder-tree"      label="Root units"        value={stats.rootUnits}         tone="mist" />
          <StatCard icon="fa-users"            label="Personnel"         value={stats.persons}           tone="success" />
          <StatCard icon="fa-mobile-screen"    label="Personnel w/ device" value={stats.personsWithDevice} tone="warning" />
        </div>
      )}

      {error && (
        <div role="alert" className="card mb-4 border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      {loading && tree.length === 0 ? (
        <div className="card px-6 py-16 text-center text-mist">
          <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
          <div className="text-sm">Loading OSS…</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <div className="min-w-0">
            <UnitTree
              roots={tree}
              selection={selection}
              onSelect={setSelection}
              onAddSubUnit={openCreateChild}
              onAddPerson={openAddPerson}
              onEditUnit={openEditUnit}
              onEditPerson={openEditPerson}
              onDeleteUnit={openDeleteUnit}
              onDeletePerson={openDeletePerson}
              onMoveUnit={openMoveUnit}
              onMovePerson={openMovePerson}
              isAdmin={isAdmin}
              inheritedHome={inheritedMap}
            />
          </div>
          <div className="min-w-0">
            <OssDetail
              selection={selection}
              units={lookups.units}
              persons={lookups.persons}
              nodes={nodesById}
              ancestry={ancestry}
              inheritedHome={selection?.kind === 'unit' ? inheritedMap[selection.id] : null}
              viewerTimezone={currentUser?.timezone || 'UTC'}
              isAdmin={isAdmin}
              onAddSubUnit={openCreateChild}
              onAddPerson={openAddPerson}
              onEditUnit={openEditUnit}
              onEditPerson={openEditPerson}
              onDeleteUnit={openDeleteUnit}
              onDeletePerson={openDeletePerson}
              onMoveUnit={openMoveUnit}
              onMovePerson={openMovePerson}
            />
          </div>
        </div>
      )}

      <UnitFormModal
        open={unitFormState.open}
        mode={unitFormState.mode}
        parentName={unitFormState.parent?.name}
        initial={unitFormState.initial}
        onClose={() => setUnitFormState({ open: false, mode: 'create', parent: null, initial: null })}
        onSave={handleUnitSave}
      />

      <PersonFormModal
        open={personFormState.open}
        mode={personFormState.mode}
        unitName={personFormState.unit?.name}
        initial={personFormState.initial}
        onClose={() => setPersonFormState({ open: false, mode: 'create', unit: null, initial: null })}
        onSave={handlePersonSave}
      />

      <MoveModal
        open={moveState.open}
        mode={moveState.mode}
        item={moveState.item}
        flatUnits={moveFlatUnits}
        allowRoot={moveState.mode === 'unit'}
        onClose={() => setMoveState({ open: false, mode: 'unit', item: null })}
        onConfirm={handleMoveConfirm}
      />

      <ConfirmDialog
        open={deleteState.open}
        title={deleteState.kind === 'unit'
          ? `Delete unit "${deleteState.item?.name}"?`
          : `Delete person "${deleteState.item?.displayName}"?`}
        message={deleteState.kind === 'unit'
          ? 'A unit can only be deleted when it has no sub-units and no personnel. Move them first if needed.'
          : 'This permanently removes the personnel record.'}
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleteState({ open: false, kind: null, item: null })}
        onConfirm={handleDelete}
      />
    </PageShell>
  );
}

function StatCard({ icon, label, value, tone = 'accent' }) {
  const tones = {
    accent:  'bg-accent-soft text-accent',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    rose:    'bg-rose-soft text-rose-700',
    mist:    'bg-canvas text-mist',
  };
  return (
    <div className="card flex items-center justify-between px-5 py-4">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
        <div className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">{value}</div>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone]}`}>
        <i className={`fa-solid ${icon} text-lg`} aria-hidden="true" />
      </div>
    </div>
  );
}
